import { captureException, sendFeedback } from '@sentry/react'
import { ReactNode, useState } from 'react'
import { Heading } from 'react-aria-components'
import { useMatch } from 'react-router-dom'
import { ConfirmationMessage } from '~/components/ErrorBoundary/components/ConfirmationMessage'
import { FacilityDropDown } from '~/components/ErrorBoundary/components/FacilityDropDown'
import { FileDragAndDrop } from '~/components/ErrorBoundary/components/FileDragAndDrop'
import { useFeedbackDialogContext } from '~/components/ErrorBoundary/context'
import { useAppSelector } from '~/store/hooks'
import { Alert } from '~/ui/Alert'
import { Button } from '~/ui/Button'
import { Form } from '~/ui/Form'
import { TextField } from '~/ui/TextField'

export function FeedbackForm({
  errorEventId,
  actions,
  onFeedbackComplete,
}: {
  errorEventId?: string
  actions?: ReactNode
  onFeedbackComplete: () => void
}) {
  const facilityUrlMatch = useMatch('/facility/:id/area/:floorId/version/:versionId')
  const { name, email } = useAppSelector(state => state.auth)
  const { facilityId, files, message, setFormData, resetFormData } = useFeedbackDialogContext()
  const [feedbackResult, setFeedbackResult] = useState<string | 'loading' | undefined | Error>()
  const isUncaughtException = !!errorEventId
  const isLoading = feedbackResult === 'loading'

  if (feedbackResult != null && !(feedbackResult instanceof Error)) {
    return (
      <div className="flex flex-col gap-3">
        <ConfirmationMessage />
        <Button onClick={onFeedbackComplete}>{isUncaughtException ? 'Reload App' : 'Close'}</Button>
      </div>
    )
  }

  return (
    <>
      <Heading slot="title" className="text-lg font-bold pb-3">
        {isUncaughtException ? 'Report Crash' : 'Provide Feedback'}
      </Heading>
      <Form
        className="flex flex-col gap-3"
        onSubmit={async e => {
          e.preventDefault()
          try {
            setFeedbackResult('loading')
            const eventId = await sendFeedback(
              {
                name: name ?? undefined,
                email: email ?? undefined,
                message,
                associatedEventId: errorEventId,
                url: facilityUrlMatch
                  ? `${window.location.origin}${facilityUrlMatch.pathname}`
                  : `${window.location.origin}/facility/${facilityId ?? ''}`,
              },
              {
                attachments: await Promise.all(
                  files.map(async ({ file }) => ({
                    filename: file.name,
                    data: new Uint8Array(await file.arrayBuffer()),
                    contentType: file.type,
                  }))
                ),
              }
            )
            setFeedbackResult(eventId)
            resetFormData()
          } catch (error) {
            console.error(error)
            captureException(error)
            setFeedbackResult(error instanceof Error ? error : new Error('Failed to submit feedback'))
          }
        }}
      >
        <TextField
          isRequired
          label={isUncaughtException ? 'Describe what you were doing before SpecLab crashed' : 'Describe your issue'}
          value={message}
          onChange={value => setFormData({ message: value })}
          rows={6}
        />
        {facilityUrlMatch === null && <FacilityDropDown />}
        <FileDragAndDrop />
        {feedbackResult instanceof Error && <Alert severity="error">{feedbackResult.message}</Alert>}
        <div className="flex flex-row gap-3 justify-end">
          {actions}
          <Button isDisabled={isLoading} type="submit">
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </Form>
    </>
  )
}
